/**
 * useAtelierEntitlement.js
 *
 * Lightweight hook that fetches the current user's Atelier access
 * state from /api/atelier/entitlement. Used by:
 *   - <AtelierNavBadge/>  — to decide whether to show the top-nav
 *     entry at all
 *   - <AtelierHub/>       — to render the "Begin New Dream" CTA only
 *     for entitled users
 *   - The Stripe checkout flow (later)
 *
 * Keep it small. No global state, no react-query — the entitlement
 * call is cheap and called from few places.
 */
import { useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

export function useAtelierEntitlement() {
  const [state, setState] = useState({
    loading: true,
    has_access: false,
    credits_available: 0,
    credits_used: 0,
    free_credit_remaining: false,
    membership_tier: "free",
    price_eur: 49,
    error: null,
  });

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      setState((s) => ({ ...s, loading: false, has_access: false }));
      return;
    }
    try {
      // `cache: "no-store"` + bust-param: iOS Safari (and CloudFront in
      // front of the review subdomain) were serving stale entitlement
      // responses from when the user had 0 credits, even after a fresh
      // grant. Force-fresh every time — it's a tiny JSON payload.
      const r = await fetch(`${API}/api/atelier/entitlement?_=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!r.ok) throw new Error(`entitlement_${r.status}`);
      const d = await r.json();
      setState({ ...d, loading: false, error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: String(e) }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}
